import LightPopin from '@scripts/components/LightPopin';
import FormPartial from '@scripts/forms/FormPartial';
import BasicDetailsForm from '@scripts/forms/BasicDetailsForm';
import HealthStatusForm from '@scripts/forms/HealthStatusForm';
import AdditionalBiomarkersForm from '@scripts/forms/AdditionalBiomarkersForm';
import IcrasHelpers from '@scripts/helpers';

/**
 * Global icras form
 */
export default class {
  /**
   * @private
   *
   * @type {HTMLFormElement}
   */
  #element;

  /**
   * @private
   *
   * @type {LightPopin}
   */
  #popin;

  /**
   * If a submit button should be added to the popin content
   *
   * @private
   *
   * @type {bool}
   */
  #popinSubmit = false;

  /**
   * @private
   *
   * @type {BasicDetailsForm}
   */
  #basicDetails;

	  /**
   * @private
   *
   * @type {HealthStatusForm}
   */
  #healthStatus;

	  /**
   * @private
   *
   * @type {AdditionalBiomarkersForm}
   */
  #additionalBiomarkers;

  /**
   * Input used to detect if PDF file must be generated
   *
   * @private
   *
   * @type {?HTMLInputElement}
   */
  #inputPdf;

	#boundCalculateEGFR;

  /**
   * Error messages
   *
   * @private
   *
   * @type {Array<string>}
   */
  #errors = [];

  /**
   * Warning messages
   *
   * @private
   *
   * @type {Array<string>}
   */
  #warnings = [];

  /**
   * Success messages
   *
   * @private
   *
   * @type {Array<string>}
   */
  #success = [];

  /**
   * Currently being submitted
   *
   * @private
   *
   * @type {bool}
   */
  #processSubmit = false;

  constructor() {
    this.#element = document.getElementById('icrasForm');
    if (!this.#element) {
      throw new Error('icras: form does not exist');
    }

    this.#popin = new LightPopin();
    this.#basicDetails = new BasicDetailsForm(this);
		this.#healthStatus = new HealthStatusForm(this);
		this.#additionalBiomarkers = new AdditionalBiomarkersForm(this);
    this.#inputPdf = this.#element.querySelector('#icrasInputPdf');
		this.#boundCalculateEGFR = this.updateEgfrFromInputs.bind(this);
  }

  /**
   * Handler for form submit
   *
   * @param {Event} e
   *
   * @return {void}
   */
  #handlerFormSubmit(e) {
    e.preventDefault();
    this.submit();
  }

  /**
   * Handler for form reset
   *
   * @param {Event} e
   *
   * @return {void}
   */
  #handlerFormReset(e) {
    e.preventDefault();
    this.reset();
  }

	/**
	* Active/désactive le calcul automatique eGFR
	* @param {boolean} isEnabled
	*/
	#handleToggleAutoEgfr(e) {
		const checkbox = e.target;
		const previousState = !checkbox.checked;

		const value = this.updateEgfrFromInputs();

		if (value === false) {
			checkbox.checked = previousState;
			this.openPopin(
				IcrasHelpers.buildPopinContent(
					'',
					[IcrasHelpers.error('auto_egfr_invalid')]
				)
			);
			return;
		}

		checkbox.checked ? this.enableAutoEgfr() : this.disableAutoEgfr();
	}

  /**
   * @return {Array<FormPartial>}
   */
  #getFormParts() {
    return [this.#basicDetails, this.#healthStatus, this.#additionalBiomarkers];
  }

  /**
   * Validate a form part
   *
   * @param {string} partial Form key: 'basic-details'
   *
   * @return {bool}
   */
  validatePartial(partial) {
    switch (partial) {
      case 'basic-details':
        return this.#basicDetails.validate();
			case 'health-status':
        return this.#healthStatus.validate();
			case 'additional-biomarkers':
        return this.#additionalBiomarkers.validate();
      default:
        return false;
    }
  }

  /**
   * Initialize form parts and event listeners
   *
   * @return {void}
   */
  initialize() {
    // Reset
    this.#element
      .querySelectorAll('.icras-btn-reset')
      .forEach((btn) =>
        btn.addEventListener('click', (e) => this.#handlerFormReset(e))
      );

    // Submit
    this.#element
      .querySelectorAll('.icras-btn-submit')
      .forEach((btn) =>
        btn.addEventListener('click', (e) => this.#handlerFormSubmit(e))
      );

		 const autoEgfrCheckbox = this.#element.querySelector('#icrasCheckbox_autoegfr');

    if (autoEgfrCheckbox) {
			
        autoEgfrCheckbox.addEventListener('change', (e) => {
            this.#handleToggleAutoEgfr(e);
        });

        if (autoEgfrCheckbox.checked) {
            this.#enableAutoEgfrOnInit();
        }
    }

    // Initialize form parts
    this.#getFormParts().forEach((form) => form.initialize());
  }

	validateAutoEgfrOnSubmit() {
		const checkbox = this.#element.querySelector('#icrasCheckbox_autoegfr');
		if (!checkbox?.checked) return;

		const value = this.computeEGFR();
	}

  /**
   * Enable / Desable the generation of the PDF file
   *
   * @param {boolean} active If PDF must be generated (Default false)
   *
   * @return {void}
   */
  setPDF(active) {
    if (this.#inputPdf) {
      this.#inputPdf.value = active === true ? 1 : 0;
    }
  }

	/**
	 * 
	 * @returns the eGFR value autocalculated
	 */
	computeEGFR() {
		const age = this.#basicDetails.getAgeSliderValue();
		const gender = this.#basicDetails.getGenderValue();
		const scr = this.#healthStatus.getCrpSliderValue();

		if (!Number.isFinite(age) || !Number.isFinite(scr) || age <= 0 || scr <= 0 || !gender) {
			return false;
		}

		const isMale = gender === 'm';
		const kappa = isMale ? 0.9 : 0.7;
		const alpha = isMale ? -0.302 : -0.241;
		const sexFactor = isMale ? 1 : 1.012;

		const minScr = Math.min(scr / kappa, 1);
		const maxScr = Math.max(scr / kappa, 1);

		return Number(
			(
				142 *
				Math.pow(minScr, alpha) *
				Math.pow(maxScr, -1.2) *
				Math.pow(0.9938, age) *
				sexFactor
			).toFixed(2)
		);
	}

	setCalculateEGFR(value) {
			this.#healthStatus.setEgfrValue(value);
	}

	updateEgfrFromInputs() {
		const value = this.computeEGFR();
		if (value !== false) {
			this.#healthStatus.setEgfrValue(value);
		}
		return value;
	}

	enableAutoEgfr() {
		this.#healthStatus.onCrpChange(this.#boundCalculateEGFR);
		this.#basicDetails.onAgeChange(this.#boundCalculateEGFR);
		this.#basicDetails.onGenderChange(this.#boundCalculateEGFR);

		this.#healthStatus.toggleAutoEgfr(true);
	}

	disableAutoEgfr() {
		this.#healthStatus.offCrpChange(this.#boundCalculateEGFR);
		this.#basicDetails.offAgeChange(this.#boundCalculateEGFR);
		this.#basicDetails.offGenderChange(this.#boundCalculateEGFR);

		this.#healthStatus.toggleAutoEgfr(false);
	}

	initializeAutoEgfr() {
		const checkbox = this.#element.querySelector('#icrasCheckbox_autoegfr');
		if (!checkbox || !checkbox.checked) return;

		const value = this.updateEgfrFromInputs();
		if (value !== false) {
			this.enableAutoEgfr();
		} else {
			checkbox.checked = false;
		}
	}


  /**
   * Submit the form or display a popin with error messages
   *
   * @return {void}
   */
  submit() {
    this.#processSubmit = true;

    // reset messages
    this.#errors = [];
    this.#warnings = [];
    this.#success = [];

		this.validateAutoEgfrOnSubmit();

    this.#getFormParts().every((form) => {
      form.validate();

      // errors
      this.#errors = [...this.#errors, ...form.errors];

      // warnings
      this.#warnings = [...this.#warnings, ...form.warnings];

      // success
      this.#success = [...this.#success, ...form.success];

      return this.#errors.length === 0;
    });

    const popinContent = IcrasHelpers.buildPopinContent(
      '',
      this.#errors,
      this.#warnings,
      this.#success
    );

    if (popinContent) {
      this.openPopin(popinContent);
    } else {
      this.#element.submit();
    }

    this.#processSubmit = false;
  }


  /**
   * Reset each partial form
   *
   * @return {void}
   */
  reset() {
    this.#basicDetails.reset();
		this.#healthStatus.reset();
		this.#additionalBiomarkers.reset();
  }

  /**
   * Attaches an event listener to the element
   *
   * @param {string} event Event name
   * @param {function} cb Callback function
   * @param {object} options Listener options
   *
   * @return {void}
   */
  on(event, cb, options = {}) {
    this.#element.addEventListener(event, cb, options);
  }

  /**
   * Detaches an event listener from the element
   *
   * @param {string} event Event name
   * @param {function} cb Callback function
   * @param {object} options Listener options
   *
   * @return {void}
   */
  off(event, cb, options = {}) {
    this.#element.removeEventListener(event, cb, options);
  }

  /**
   * Dispatch an event to the element
   *
   * @param {string} event Event name
   * @param {object} data Event data
   *
   * @return {void}
   */
  dispatch(event, data = {}) {
    this.#element.dispatchEvent(new CustomEvent(event, { detail: data }));
  }

  /**
   * Open the popin
   *
   * NOTICE: add a submit button if required
   *
   * @param {string} content Popin content
   *
   * @return {void}
   */
  openPopin(content) {
    if (this.#popinSubmit) {
      // success message
      content += IcrasHelpers.buildAlertSuccess([
        IcrasHelpers.success('can_submit'),
      ]).outerHTML;

      // submit button
      let wrapper = document.createElement('div');
      wrapper.classList.add(
        'icras-mt-4',
        'icras-flex',
        'icras-items-center',
        'icras-justify-center'
      );
      let submit = document.createElement('button');
      submit.id = 'icrasPopinBtnSubmit';
      submit.classList.add('icras-btn-submit-primary');
      submit.type = 'submit';
      submit.innerText = IcrasHelpers.label('submit_continue') ?? 'Submit';
      submit.addEventListener('click', (e) => this.#element.submit());

      wrapper.appendChild(submit);
      content += wrapper.outerHTML;
    }

    this.#popin.open(content);

    if (this.#popinSubmit) {
      document
        .getElementById('icrasPopinBtnSubmit')
        .addEventListener('click', (e) => this.#element.submit());

      // reset submit button
      this.#popinSubmit = false;
    }
  }

  /**
   * Download the PDF
   *
   * @return {void}
   */
  downloadPDF() {
    this.setPDF(true);
    this.#element.submit();
    this.setPDF(false);
  }

  /**
   * Get the form element
   *
   * @return {HTMLFormElement}
   */
  get element() {
    return this.#element;
  }

  /**
   * If the form is being submitted
   *
   * @return {bool}
   */
  get beingSubmitted() {
    return this.#processSubmit;
  }

  /**
   * Set whether a submit button should be added to the popin content
   *
   * @param {bool} value
   *
   * @return {void}
   */
  set withPopinSubmit(value = false) {
    this.#popinSubmit = value;
  }

	#enableAutoEgfrOnInit() {
    const egfrValue = this.updateEgfrFromInputs();

    if (egfrValue !== false) {
        this.setCalculateEGFR(egfrValue);

        this.#healthStatus.onCrpChange(this.#boundCalculateEGFR);
        this.#basicDetails.onAgeChange(this.#boundCalculateEGFR);
        this.#basicDetails.onGenderChange(this.#boundCalculateEGFR);

        this.#healthStatus.toggleAutoEgfr(true);
    } else {
        // sécurité : checkbox cochée mais données invalides
        const checkbox = this.#element.querySelector('#icrasAutoEgfr');
        if (checkbox) checkbox.checked = false;
    }
	}

}
